import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import DrawerSave from './DrawerSave';
import translation from '../../../constants/SaveTranslation.json';
import HookFormErrorFocus from '../Forms/HookFormErrorFocus';
import { ApplicationState } from '../../../reducers';
import { hasBrandEditPermission } from '../../../selectors/userSelector';

type DrawerHookFormProps = {
  defaultValues: any;
  visible: boolean;
  validationSchema?: any;
  className?: string;
  title?: React.ReactNode;
  width?: string | number;
  style?: React.CSSProperties;
  isLoading?: boolean;
  destroyOnClose?: boolean;
  push?: boolean | { distance: number | string };
  onSubmit: (values: any, setError: any) => void | Promise<void>;
  onClose: () => void;
  onDelete?: (values?: any) => void;
  onNew?: (dirty?: boolean) => void;
  onNext?: (dirty?: boolean) => void;
  onPrev?: (dirty?: boolean) => void;
  children: (formMethods: any) => React.ReactNode;
  handleSaveButtonEnabled?: (formState: any, getValues: any) => boolean;
  handleAdditionalFooterData?: (formState: any) => React.ReactNode;
  saveButtonEnabled?: boolean;
  deleteButtonEnabled?: boolean;
  newButtonEnabled?: boolean;
  additionalFooterData?: React.ReactNode;
  enableReinitialize?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
};

const DrawerHookForm: React.FC<DrawerHookFormProps> = props => {
  const { modal } = App.useApp();

  const brandId = useSelector((state: ApplicationState) => state.parent.brands.selectedBrandId);
  const brandEditPermission = useSelector((state: ApplicationState) =>
    hasBrandEditPermission(state)
  );

  const formMethods = useForm({
    defaultValues: props.defaultValues,
    mode: props.validateOnBlur ? 'onBlur' : 'onTouched',
    resolver: props.validationSchema ? yupResolver(props.validationSchema) : undefined,
  });

  const { handleSubmit, reset, getValues, setError, formState } = formMethods;
  const { isDirty, isSubmitting } = formState;

  React.useEffect(() => {
    if (props.enableReinitialize) {
      reset(props.defaultValues);
    }
  }, [props.defaultValues, props.enableReinitialize, reset]);

  const handleClose = (force?: boolean) => {
    if (!isDirty || force) {
      props.onClose();
    } else {
      modal.confirm({
        title: translation.closeWarningTitle,
        icon: <ExclamationCircleOutlined />,
        onOk() {
          props.onClose();
        },
      });
    }
  };

  const onSubmit = async (values: any) => {
    await props.onSubmit(values, setError);
  };

  const saveButtonEnabled = props.handleSaveButtonEnabled
    ? props.handleSaveButtonEnabled(formState, getValues)
    : props.saveButtonEnabled || (isDirty && (!brandId || (brandId && brandEditPermission)));

  const newButtonEnabled = isDirty || props.newButtonEnabled;

  const deleteButtonEnabled =
    props.deleteButtonEnabled !== undefined ? props.deleteButtonEnabled : !!props.onDelete;
  const additionalFooterData = props.handleAdditionalFooterData
    ? props.handleAdditionalFooterData(formState)
    : props.additionalFooterData;

  return props.visible ? (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerSave
          title={props.title}
          className={props.className}
          onClose={force => handleClose(force)}
          onSave={handleSubmit(onSubmit)}
          onDelete={props.onDelete ? () => props.onDelete!(formMethods.getValues()) : undefined}
          onNext={props.onNext ? () => props.onNext!(isDirty) : undefined}
          onNew={props.onNew ? () => props.onNew!(isDirty) : undefined}
          onPrev={props.onPrev ? () => props.onPrev!(isDirty) : undefined}
          visible={props.visible}
          width={props.width}
          style={props.style}
          saveButtonEnabled={saveButtonEnabled}
          deleteButtonEnabled={deleteButtonEnabled}
          newButtonEnabled={newButtonEnabled}
          additionalFooterData={additionalFooterData}
          isSubmitting={isSubmitting}
          isLoading={props.isLoading}
          destroyOnClose={props.destroyOnClose}
          push={props.push}
        >
          {props.children({
            ...formMethods,
            formState,
          })}
        </DrawerSave>
        <HookFormErrorFocus />
      </form>
    </FormProvider>
  ) : null;
};

export default DrawerHookForm;
